import { useEffect, useState } from 'react';
import {
  canInstall,
  installInstructions,
  isInstalled,
  platform,
  promptInstall,
  subscribeInstall,
} from '../lib/pwa';

const DISMISS_KEY = 'norush.install-banner.dismissed';

/**
 * Invitation to add No Rush to the home screen. One "Install" button that
 * does the best available thing on click:
 *   - if the browser captured an install prompt (Android/desktop Chrome),
 *     it fires the real native "add to home screen" dialog — one tap;
 *   - otherwise (iOS, or Chrome not ready yet), it reveals the manual
 *     steps inline.
 *
 * Hidden once installed (standalone) or dismissed. Shows on mobile always,
 * and on desktop only when a real prompt is available (to avoid noise).
 */
export function InstallBanner() {
  const [, force] = useState(0);
  const [steps, setSteps] = useState(false);
  const [dismissed, setDismissed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(DISMISS_KEY) === '1';
    } catch {
      return false;
    }
  });

  useEffect(() => subscribeInstall(() => force((n) => n + 1)), []);

  if (dismissed || isInstalled()) return null;
  const mobile = platform() === 'ios' || platform() === 'android';
  if (!mobile && !canInstall()) return null;

  function dismiss() {
    try {
      localStorage.setItem(DISMISS_KEY, '1');
    } catch {
      // ignore
    }
    setDismissed(true);
  }

  async function install() {
    const outcome = await promptInstall();
    if (outcome === 'unavailable') {
      // No native prompt (iOS, or Chrome not ready) — show how to do it.
      setSteps(true);
    } else {
      // Accepted or cancelled the OS dialog — either way, don't nag again.
      dismiss();
    }
  }

  return (
    <div className="install-banner" role="dialog" aria-label="Install No Rush">
      <span className="install-banner-text">
        {steps
          ? installInstructions()
          : 'Add No Rush to your home screen for one tap. Still no notifications.'}
      </span>
      <div className="install-banner-actions">
        <button type="button" className="btn btn-ghost btn-small" onClick={dismiss}>
          {steps ? 'Got it' : 'Not now'}
        </button>
        {!steps && (
          <button type="button" className="btn btn-primary btn-small" onClick={install}>
            Install
          </button>
        )}
      </div>
    </div>
  );
}
